import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  Renderer2,
  SimpleChanges,
} from '@angular/core';

/**
 * Format for CSS properties w/ multiple sides
 * string  — for margin/padding, value is a CSS measurement, e.g. "24px" or "1.5rem"
 * boolean — for border, value represents whether border is applied on [side]
 */
interface BoxObject {
  top: string | boolean;
  right: string | boolean;
  bottom: string | boolean;
  left: string | boolean;
}

/**
 * Extension for configurable border properties
 */
interface BorderObject extends BoxObject {
  color: string;
  style?: 'solid' | 'dashed' | 'dotted';
  radius?: string;
  width: string;
}

/**
 * Format for configurable box model
 * @example
 *   {
 *      "padding": {
 *          "top": "16px",
 *          "right": "1.5rem",
 *          "bottom": "12.5%",
 *          "left": "10vw"
 *      },
 *      "margin": {
 *          "top": "0px",
 *          "right": "0px",
 *          "bottom": "0px",
 *          "left": "0px"
 *      },
 *      "border": {
 *          "color": "var(--gray--border)",
 *          "style": "solid",
 *          "width": "1px",
 *          "top": "true",
 *          "right": "true",
 *          "bottom": "true",
 *          "left": "true"
 *      },
 *      "backgroundColor": "var(--primary)"
 *   }
 */
export interface UIBoxModelConfig {
  margin: BoxObject;
  padding: BoxObject;
  border: BorderObject;
  backgroundColor?: string;
  width: string;
}

/**
 * Sets CSS box model properties for a given HTML element based on configuration
 * @example
 *   <div [ui-box-model]="config"></div>
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[ui-box-model]',
})
export class UIBoxModelDirective implements AfterViewInit, OnChanges {
  @Input('ui-box-model') config!: UIBoxModelConfig;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
  ) {}

  ngAfterViewInit() {
    this.applyStyles();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      this.applyStyles();
    }
  }

  applyStyles() {
    const boxModel = this.getBoxModel(this.config);
    const el = this.elementRef.nativeElement;

    this.renderer.setStyle(el, 'background-color', boxModel.backgroundColor);
    this.renderer.setStyle(el, 'margin', boxModel.margin);
    this.renderer.setStyle(el, 'padding', boxModel.padding);
    this.renderer.setStyle(el, 'width', boxModel.width);
    this.renderer.setStyle(el, 'border-color', boxModel.border?.color);
    this.renderer.setStyle(el, 'border-style', boxModel.border?.style);
    this.renderer.setStyle(el, 'border-width', boxModel.border?.width);
    this.renderer.setStyle(el, 'border-radius', boxModel.border?.radius);
  }

  /**
   * Transforms the box model configuration to a CSS string for either margin or padding
   * @example
   * // returns "0 2px 4px 0"
   * this.getMarginPadding({ top: '0px', right: '2px', bottom: '4px', left: '0px' }, 'margin')
   * @returns {string} Returns the CSS margin/padding string.
   */
  getMarginPadding(config: any, property: 'margin' | 'padding'): string {
    function mp(direction: string, ctx = config[property]) {
      return ctx[direction] || '0';
    }

    return config[property]
      ? `${mp('top')} ${mp('right')} ${mp('bottom')} ${mp('left')}`
      : '0';
  }

  /**
   * Combines border side boolean values w/ width & returns formatted config object
   * @example
   * // returns {
   *      style: 'solid',
   *      color: 'var(--gray--border)',
   *      width: 0 2px 2px 2px,
   *      radius: 0
   *    }
   * this.getBorder({
   *      top: null,
   *      right: true,
   *      bottom: true,
   *      left: true,
   *      width: '2px',
   *      color: 'var(--gray--border)'
   * })
   * @returns {object} Returns the formatted border configuration object.
   */
  getBorder(config: BorderObject) {
    function bw(direction: 'top' | 'right' | 'bottom' | 'left', ctx = config) {
      return ctx[direction] ? ctx['width'] : '0';
    }

    if (config) {
      const color = config['color'];
      const style = config['style'] || 'solid';
      const radius = config['radius'] || 0;
      const width = `${bw('top')} ${bw('right')} ${bw('bottom')} ${bw('left')}`;

      return {
        style,
        color,
        width,
        radius,
      };
    }

    return null;
  }

  /**
   * Transforms configuration into legible CSS strings for Renderer2
   * @param {UIBoxModelConfig} config
   */
  getBoxModel(config: UIBoxModelConfig) {
    return {
      margin: this.getMarginPadding(config, 'margin'),
      padding: this.getMarginPadding(config, 'padding'),
      border: this.getBorder(config.border),
      width: config.width,
      backgroundColor: config.backgroundColor,
    };
  }
}
